/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { CardInteractiveArea, Grid, Link, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { Maybe } from 'bundles/unified-career-academy/__generated__/baseContentfulTypes';
import { CAREER_CARD_OVERRIDE_PARTNER_LOGOS } from 'bundles/unified-career-academy/constants';
import type { Asset } from 'bundles/unified-career-academy/types';

import _t from 'i18n!nls/career-academy';

export type Props = {
  credentials?:
    | ({
        slug?: string | null;
        programName?: string | null;
        localizations?: Maybe<Maybe<string>[]>;
        partnerInformationCollection?: {
          items?: ({ partnerLogo?: Asset; partnerSlug?: string | null } | null)[] | null;
        } | null;
      } | null)[]
    | null;
  customLogoDimenions?: {
    height?: string;
    width?: string;
  };
  linkVariant?: 'bodyPrimary' | 'bodySecondary';
};

const style = {
  partnerLogo: (customLogoDimenions?: { height?: string; width?: string }) => css`
    display: inline-flex;
    border: 1px solid var(--cds-color-neutral-primary-weak);
    border-radius: 4px;
    height: ${customLogoDimenions?.height ?? '40px'};
    width: ${customLogoDimenions?.width ?? '40px'};
    padding: var(--cds-spacing-50);
    margin-right: var(--cds-spacing-150);
    align-items: center;

    img {
      width: 100%;
    }
  `,
};

const cleanProgramName = (name: string) => name.replace('New!', '').replace('Professional Certificate', '').trim();

const CareerCardCredentials: React.FC<Props> = ({ credentials, customLogoDimenions, linkVariant = 'bodyPrimary' }) => {
  const track = useTracker();

  return (
    <Grid container spacing={8} direction="column">
      <Grid item>
        <Typography2 component="h3" variant="subtitleMedium">
          {_t('Credentials from leading partners')}
        </Typography2>
      </Grid>
      {credentials?.map((cred) => {
        const logo = cred?.partnerInformationCollection?.items?.[0]?.partnerLogo;
        const partnerSlug = cred?.partnerInformationCollection?.items?.[0]?.partnerSlug;
        const logoUrl =
          partnerSlug && CAREER_CARD_OVERRIDE_PARTNER_LOGOS[partnerSlug]
            ? CAREER_CARD_OVERRIDE_PARTNER_LOGOS[partnerSlug]
            : logo?.url;

        if (!logoUrl || !cred?.programName || !cred?.slug) {
          return null;
        } else {
          const name = cleanProgramName(cred.programName);
          return (
            <Grid item container alignItems="center" key={cred.slug}>
              <CardInteractiveArea>
                <Link
                  variant="quiet"
                  typographyVariant={linkVariant}
                  href={`/professional-certificates/${cred.slug}`}
                  onClick={() => {
                    track('click_button', {
                      button: {
                        linkURL: `/professional-certificates/${cred.slug}`,
                        linkName: 'role_card_xdp_link',
                        linkType: 'internal',
                        name: 'program_info',
                      },
                      product: {
                        id: cred.slug ?? '',
                        slug: cred.slug ?? '',
                        type: 'premium_certificate',
                        name,
                      },
                    });
                  }}
                >
                  <Grid container alignItems="center" wrap="nowrap">
                    <div css={style.partnerLogo(customLogoDimenions)}>
                      <Imgix src={logoUrl} alt={logo?.description || ''} />
                    </div>
                    {name}
                  </Grid>
                </Link>
              </CardInteractiveArea>
            </Grid>
          );
        }
      })}
    </Grid>
  );
};

export default CareerCardCredentials;
